import React, { useState, useEffect } from "react";
import DashboardAnnouncement from "./DashboardAnnouncement";
import DashboardStatBlock from "./DashboardStatBlock";
import DashboardUsageChart from "./DashboardUsageChart";
import DashboardDayStats from "./DashboardDayStats";
import DashboardPrograms from "./DashboardPrograms";
import DashboardCustomerStats from "./DashboardCustomerStats";
import DashboardAverageBookings from "./DashboardAverageBookings";
import DashboardCustomizeButton from "./DashboardCustomizeButton";
import { fetchDashboardStats } from "../../../../api/host.api";
import { fetchDomesByHost } from "../../../../api/dome.api";
import DomeMap from "../../../components/DomeMap";
import "../host.css";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domes, setDomes] = useState([]);

  useEffect(() => {
    const loadDashboardStats = async () => {
      try {
        setLoading(true);
        const dashboardData = await fetchDashboardStats();
        console.log('Dashboard data:', dashboardData); // Debug log
        setStats(dashboardData);

        // Fetch Domes for Map
        const hostDetailsRaw = localStorage.getItem('hostDetails');
        if (hostDetailsRaw) {
          const hostDetails = JSON.parse(hostDetailsRaw);
          const hostId = hostDetails.hostId || hostDetails._id;
          if (hostId) {
            const domesData = await fetchDomesByHost(hostId, '', 1, 100); // Fetch up to 100 domes
            if (domesData && domesData.domes) {
              setDomes(domesData.domes);
            }
          }
        }

      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardStats();
  }, []);

  const hostDetailsRaw = localStorage.getItem('hostDetails');
  const hostMarket = hostDetailsRaw ? JSON.parse(hostDetailsRaw).market?.trim().toLowerCase() : 'market facing';
  const isMarketFacing = hostMarket === 'market facing';

  if (loading) {
    return (
      <div className="container-fluid dashboard-full-height d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid dashboard-full-height d-flex justify-content-center align-items-center">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      className="container-fluid dashboard-full-height"
      style={{
        display: "grid",
        gridTemplateRows: "auto auto auto auto", // Added auto for 4th row
        gap: "0.5rem",
      }}
    >
      <div className="row g-3 mb-2 align-items h-80">
        <div className={`col-lg-${isMarketFacing ? '6' : '8'} col-xl-${isMarketFacing ? '6' : '8'}`}>
          <DashboardAnnouncement stats={stats} />
        </div>
        {isMarketFacing && (
          <div className="col-6 col-lg-2 col-xl-2">
            <DashboardStatBlock type="earnings" stats={stats} />
          </div>
        )}
        <div className={`col-6 col-lg-${isMarketFacing ? '2' : '2'} col-xl-${isMarketFacing ? '2' : '2'}`}>
          <DashboardStatBlock type="bookings" stats={stats} />
        </div>
        <div className={`col-6 col-lg-${isMarketFacing ? '2' : '2'} col-xl-${isMarketFacing ? '2' : '2'}`}>
          <DashboardStatBlock type="upcoming" stats={stats} />
        </div>
      </div>
      <div className="row g-3 mb-2 h-80">
        <div className="col-lg-9">
          <DashboardUsageChart stats={stats} />
        </div>
        <div className="col-lg-3">
          <DashboardDayStats stats={stats} />
        </div>
      </div>
      <div className="row g-3 mb-3 align-items h-80">
        <div className={`col-lg-${isMarketFacing ? '4' : '12'}`}>
          <div className="h-100">
            <DashboardPrograms stats={stats} />
          </div>
        </div>
        {isMarketFacing && (
          <>
            <div className="col-lg-3">
              <div className="h-100 d-flex flex-column">
                <DashboardCustomerStats stats={stats} />
              </div>
            </div>
            <div className="col-lg-5">
              <div className="h-100 d-flex flex-column gap-3">
                <DashboardAverageBookings stats={stats} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dome Locator Map Row */}
      <div className="row g-3 mb-3">
        <div className="col-12">
          <div className="card h-100">
            <div className="card-header border-0 pb-0">
              <h4 className="card-title">Dome Locator</h4>
            </div>
            <div className="card-body">
              <DomeMap domes={domes} />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;